import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Loader2, ServerCrash } from "lucide-react";
import { lazy, Suspense, useLayoutEffect, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Layout } from "./components/Layout";
import { ConfirmProvider, ToastProvider } from "./components/overlay";
import { LangProvider, t } from "./i18n";
import { Spinner } from "./components/ui";
import { ApiError } from "./services/api";
import { AuthProvider, useAuth } from "./services/auth";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";

const Domains = lazy(() => import("./pages/Domains"));
const DomainDetail = lazy(() => import("./pages/DomainDetail"));
const UrlExplorer = lazy(() => import("./pages/UrlExplorer"));
const Jobs = lazy(() => import("./pages/Jobs"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const BatchDetail = lazy(() => import("./pages/BatchDetail"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const UsersPage = lazy(() => import("./pages/Users"));
const Checker = lazy(() => import("./pages/Checker"));
const SinglePage = lazy(() => import("./pages/SinglePage"));
const Hunter = lazy(() => import("./pages/Hunter"));
const HunterDetail = lazy(() => import("./pages/Hunter").then(m => ({ default: m.HunterDetail })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => !(err instanceof ApiError && [401, 403, 404, 422].includes(err.status)) && count < 2,
      refetchOnWindowFocus: false,
      staleTime: 5000,
    },
  },
});

function Mounted() {
  // Tells public/boot.js that React rendered, so its failure watchdog stands down. Layout effect: runs
  // before the first paint, so the signal is never later than the visible UI.
  useLayoutEffect(() => {
    (window as any).__ADR_MOUNTED__?.();
  }, []);
  return null;
}

function FullScreen({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center p-6">{children}</div>;
}

function Guard({ children }: { children: ReactNode }) {
  const { state, recheck } = useAuth();
  const location = useLocation();
  if (state.status === "checking") {
    return (
      <FullScreen>
        <div className="flex items-center gap-2 text-slate-500" role="status">
          <Loader2 className="h-5 w-5 animate-spin" /> {t("Checking session…")}
        </div>
      </FullScreen>
    );
  }
  if (state.status === "api_error") {
    return (
      <FullScreen>
        <div className="card-pad max-w-md text-center" role="alert">
          <ServerCrash className="mx-auto h-8 w-8 text-red-500" />
          <h1 className="mt-2 text-lg font-semibold">{t("Cannot reach the API")}</h1>
          <p className="mt-1 text-sm text-slate-500">{state.message}</p>
          <p className="mt-1 text-xs text-slate-500">{t("Recovery jobs already queued keep running in the background workers.")}</p>
          <button className="btn-primary mt-4" onClick={recheck}>
            {t("Retry")}
          </button>
        </div>
      </FullScreen>
    );
  }
  if (state.status === "anonymous") return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <>{children}</>;
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Spinner label={t("Loading page…")} />}>{children}</Suspense>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
      <ConfirmProvider>
      <AuthProvider>
        <BrowserRouter>
          <Mounted />
          <LangProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              element={
                <Guard>
                  <Layout />
                </Guard>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="domains" element={<Lazy><Domains /></Lazy>} />
              <Route path="domains/:id" element={<Lazy><DomainDetail /></Lazy>} />
              <Route path="domains/:id/urls" element={<Lazy><UrlExplorer /></Lazy>} />
              <Route path="jobs" element={<Lazy><Jobs /></Lazy>} />
              <Route path="jobs/:id" element={<Lazy><JobDetail /></Lazy>} />
              <Route path="batches" element={<Navigate to="/domains" replace />} />
              <Route path="batches/:id" element={<Lazy><BatchDetail /></Lazy>} />
              <Route path="exports" element={<Navigate to="/jobs?filter=completed" replace />} />
              <Route path="settings" element={<Lazy><SettingsPage /></Lazy>} />
              <Route path="users" element={<Lazy><UsersPage /></Lazy>} />
              <Route path="tools/single-page" element={<Lazy><SinglePage /></Lazy>} />
              <Route path="checker" element={<Lazy><Checker /></Lazy>} />
              <Route path="hunter" element={<Navigate to="/hunter/search" replace />} />
              <Route path="hunter/domains/:id" element={<Lazy><HunterDetail /></Lazy>} />
              <Route path="hunter/:section" element={<Lazy><Hunter /></Lazy>} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          </LangProvider>
        </BrowserRouter>
      </AuthProvider>
      </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

function NotFound() {
  return (
    <div className="card-pad">
      <h1 className="text-lg font-semibold">{t("Page not found")}</h1>
      <p className="mt-1 text-sm text-slate-500">{t("This page does not exist.")}</p>
    </div>
  );
}
